import _ from 'lodash';
import { TKeystoneAffix, TClientAffixData } from '../../types/keystoneAffixTypes';
import { slugify } from '../slugify';
import { groupByDictUnique } from '../utils';
import { getAffixName } from './getAffixName';

export class KeystoneAffixHelper<T extends TKeystoneAffix | TClientAffixData> {
  readonly affixesBySlug: Record<string, T>;
  readonly affixesById: Record<string, T>;
  readonly affixes: T[];

  constructor(affixes: T[]) {
    this.affixesBySlug = groupByDictUnique(affixes, 'slug');
    this.affixesById = groupByDictUnique(affixes, 'id');
    this.affixes = affixes;
  }

  public getDirtyAffixSlugIdMap(): Record<string, number> {
    return this.affixes.reduce(
      (accum: Record<string, number>, item) => {
        const slug = slugify(getAffixName(item, 'en'));
        if (slug.includes('-')) {
          accum[slug] = item.id;
        }
        return accum;
      },
      {} as Record<string, number>
    );
  }

  public parseAffixesSlug(affixesSlug: string, throwErrorOnInvalidAffix = false): T[] {
    const dirtyAffixesMap = this.getDirtyAffixSlugIdMap();

    let affixes = affixesSlug;

    for (const [slug, id] of Object.entries(dirtyAffixesMap)) {
      const affix = this.affixesById[id];
      if (affix) {
        // Use split/join to replace all occurrences (ES2020 compatible)
        affixes = affixes.split(slug).join(getAffixName(affix, 'en'));
      }
    }

    return _.compact(
      affixes.split('-').map((afxName: string) => {
        const data = this.affixes.find((afx) => getAffixName(afx, 'en').toLowerCase() === afxName.toLowerCase());

        if (data) {
          return data;
        } else if (throwErrorOnInvalidAffix) {
          throw new Error(`Unknown affix ${afxName} in list`);
        }
      })
    );
  }

  public get sortedAffixes(): T[] {
    return [...this.affixes].sort((a, b) => getAffixName(a, 'en').localeCompare(getAffixName(b, 'en')));
  }
}
